import { Link } from "react-router-dom";
import { ArrowRight, Phone, Mail } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import { CONTACT } from "../data/content";
import Seo from "../components/Seo";

const Section = ({ title, children }) => (
    <Reveal>
        <div className="bg-white border border-[#E7E4DC] shadow-sm rounded-xl p-8" data-testid={`privacy-section`}>
            <h2 className="font-display text-xl sm:text-2xl text-[#20242B]">{title}</h2>
            <div className="text-[#4A4F58] text-sm sm:text-base leading-relaxed mt-4 space-y-3">{children}</div>
        </div>
    </Reveal>
);

export default function Privacy() {
    return (
        <div data-testid="page-privacy">
            <Seo
                title="Privacy Policy | Freedom Tax Filer"
                description="How Freedom Tax Filer collects, uses, and protects the information you share through our website and tax preparation services."
                path="/privacy"
            />
            <section className="relative pt-44 pb-14 bg-[#111318] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Legal</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl leading-[1.05] tracking-tight mt-8">
                        <MaskedLines delay={0.2} lines={["Privacy", <>Policy.</>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/60 text-sm mt-8">Last updated: October 2026</p>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad" data-testid="privacy-content">
                <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-5">
                    <Section title="What we collect">
                        <p>
                            When you send an inquiry through our contact form, we receive the information you provide:
                            your name, email address, optional phone number, the service you're interested in, and your
                            message. We also may collect basic, standard website usage data to improve the experience.
                        </p>
                    </Section>
                    <Section title="What we never ask for through the website">
                        <p>
                            Please don't send tax documents, Social Security numbers, account details, or other sensitive
                            information through our website form — it is for general inquiries only. If you become a
                            client, we arrange secure channels for anything sensitive.
                        </p>
                    </Section>
                    <Section title="How we use your information">
                        <p>
                            We use inquiry details for one purpose: responding to you. Submitting the form also notifies
                            our team by email so we can reply promptly. We do not sell, rent, or trade your personal
                            information.
                        </p>
                    </Section>
                    <Section title="Your choices">
                        <p>
                            Want your inquiry details updated or removed? Email us at{" "}
                            <a href={CONTACT.emailHref} className="text-[#8A6D2F] border-b border-[#C8A45D]/50 hover:border-[#C8A45D] transition-colors duration-200" data-testid="privacy-email-link">
                                {CONTACT.email}
                            </a>{" "}
                            and we'll take care of it.
                        </p>
                    </Section>
                    <Section title="Contact us about privacy">
                        <p>
                            Questions about this policy? Email{" "}
                            <a href={CONTACT.emailHref} className="text-[#8A6D2F] border-b border-[#C8A45D]/50 hover:border-[#C8A45D] transition-colors duration-200">
                                {CONTACT.email}
                            </a>.
                        </p>
                    </Section>
                    <Reveal>
                        <p className="text-[#737985] text-xs leading-relaxed">
                            This page is a plain-language summary and may be updated from time to time. If you'd like the
                            full picture of how a engagement works, ask us for the written service agreement before
                            purchasing services.
                        </p>
                    </Reveal>
                    <Reveal>
                        <Link
                            to="/contact"
                            data-testid="privacy-back-contact"
                            className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.14em] text-[#8A6D2F] border-b border-[#C8A45D]/50 pb-1 mt-4 hover:border-[#C8A45D] transition-colors duration-200"
                        >
                            Back to Contact <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
